import { oc } from "@orpc/contract";
import { z } from "zod";

/**
 * Supplier input layouts for the Results → Upload tool.
 *
 * Every supplier sends its results as an Excel workbook with its own columns,
 * but they all become the same 82-column AS400 Header Cross upload CSV. A
 * layout says, for one supplier, how each upload column is filled from that
 * supplier's workbook. Layouts are authored in the UI and evaluated in the
 * browser; the server stores them and refuses a formula that does not parse.
 */

/** The 82 columns of the AS400 Header Cross upload, `A` to `CD`. */
export const UPLOAD_COLUMNS = [
  "A",
  "B",
  "C",
  "D",
  "E",
  "F",
  "G",
  "H",
  "I",
  "J",
  "K",
  "L",
  "M",
  "N",
  "O",
  "P",
  "Q",
  "R",
  "S",
  "T",
  "U",
  "V",
  "W",
  "X",
  "Y",
  "Z",
  "AA",
  "AB",
  "AC",
  "AD",
  "AE",
  "AF",
  "AG",
  "AH",
  "AI",
  "AJ",
  "AK",
  "AL",
  "AM",
  "AN",
  "AO",
  "AP",
  "AQ",
  "AR",
  "AS",
  "AT",
  "AU",
  "AV",
  "AW",
  "AX",
  "AY",
  "AZ",
  "BA",
  "BB",
  "BC",
  "BD",
  "BE",
  "BF",
  "BG",
  "BH",
  "BI",
  "BJ",
  "BK",
  "BL",
  "BM",
  "BN",
  "BO",
  "BP",
  "BQ",
  "BR",
  "BS",
  "BT",
  "BU",
  "BV",
  "BW",
  "BX",
  "BY",
  "BZ",
  "CA",
  "CB",
  "CC",
  "CD",
] as const;

export const UploadColumnSchema = z.enum(UPLOAD_COLUMNS);

const HeaderNameSchema = z.string().trim().min(1).max(255);
const DecimalsSchema = z.number().int().min(0).max(6);

/** How a source cell's value is written. */
export const UploadSourceTransformSchema = z.discriminatedUnion("type", [
  /** As text, surrounding whitespace trimmed. */
  z.object({ type: z.literal("text") }),
  /** A date (`YYYY-MM-DD` text or an Excel date) written as AS400 `YYMMDD`. */
  z.object({ type: z.literal("date") }),
  /** A number rounded half away from zero, written with exactly N decimals. */
  z.object({ type: z.literal("number"), decimals: DecimalsSchema }),
  /** Part of the text: `length` characters from `start` (0-based), or to the end. */
  z.object({
    type: z.literal("substring"),
    start: z.number().int().min(0).max(255),
    length: z.number().int().min(1).max(255).nullable(),
  }),
]);

export const UploadColumnMappingSchema = z.discriminatedUnion("kind", [
  /** The same value on every line, e.g. `Y` or `MASS LOAD ND`. */
  z.object({ kind: z.literal("constant"), value: z.string().max(255) }),
  /** The line's position in the upload: 1, 2, 3, … */
  z.object({ kind: z.literal("rowNumber") }),
  /** A column of the supplier's workbook. */
  z.object({
    kind: z.literal("source"),
    header: HeaderNameSchema,
    transform: UploadSourceTransformSchema,
    /** Write the value only when this upload column is filled, else blank. */
    onlyWhenFilled: UploadColumnSchema.optional(),
  }),
  /** `value` when a workbook column (or another upload column) is filled, else blank. */
  z.object({
    kind: z.literal("flag"),
    when: z.union([
      z.object({ header: HeaderNameSchema }),
      z.object({ column: UploadColumnSchema }),
    ]),
    value: z.string().min(1).max(255),
  }),
  /** Arithmetic over workbook columns — see `parseUploadFormula`. */
  z.object({
    kind: z.literal("formula"),
    expression: z.string().trim().min(1).max(2000),
    /** Rounded half away from zero to this many decimals; null = as computed. */
    decimals: DecimalsSchema.nullable(),
    /** Write nothing when the result is exactly zero. */
    blankWhenZero: z.boolean(),
  }),
]);

/** How a lookup workbook's columns are named in a layout: `[label: header]`. */
export const UPLOAD_LOOKUP_SEPARATOR = ": ";

export const UploadLookupSchema = z.object({
  /** What the file is called, e.g. "Feedback"; prefixes its columns. */
  label: z
    .string()
    .trim()
    .min(1)
    .max(40)
    .regex(/^[^:[\]]+$/, "A lookup label cannot contain : [ or ]"),
  /** The results workbook column holding the key. */
  matchColumn: HeaderNameSchema,
  /** The lookup workbook column holding the same key. */
  keyColumn: HeaderNameSchema,
});

export const UploadLayoutColumnSchema = z.object({
  column: UploadColumnSchema,
  mapping: UploadColumnMappingSchema,
});

type LayoutColumnEntry = z.infer<typeof UploadLayoutColumnSchema>;

/**
 * The upload column a mapping waits on, if any: a flag that watches another
 * upload column, or a source value written only when one is filled. These are
 * filled after the rest of the line, from what the line already holds.
 */
export function watchedUploadColumn(
  mapping: LayoutColumnEntry["mapping"],
): string | null {
  if (mapping.kind === "flag" && "column" in mapping.when) {
    return mapping.when.column;
  }
  if (mapping.kind === "source") return mapping.onlyWhenFilled ?? null;
  return null;
}

/** Why a mapping that waits on another column can never see it, or null. */
function watchedColumnProblem(
  entry: LayoutColumnEntry,
  byColumn: Map<string, LayoutColumnEntry["mapping"]>,
): string | null {
  const watched = watchedUploadColumn(entry.mapping);
  if (watched === null) return null;
  const target = byColumn.get(watched);
  if (watched === entry.column) return "A column cannot wait on itself";
  if (!target) return `Column ${watched} is not mapped, so it is never filled`;
  if (watchedUploadColumn(target) !== null) {
    return `Column ${watched} itself waits on another column`;
  }
  if (target.kind === "rowNumber") {
    return `Column ${watched} is a row number, which is filled last`;
  }
  return null;
}

/**
 * Column mappings that can never produce a correct upload: a column mapped
 * twice, or a flag that watches nothing it can see. Shared by the schema (so
 * the server refuses them) and the layout editor (so it can say which row).
 */
export function uploadLayoutColumnProblems(
  columns: LayoutColumnEntry[],
): { index: number; message: string }[] {
  const byColumn = new Map(
    columns.map(({ column, mapping }) => [column, mapping]),
  );
  const seen = new Set<string>();
  return columns.flatMap((entry, index) => {
    const duplicate = seen.has(entry.column);
    seen.add(entry.column);
    const message = duplicate
      ? `Column ${entry.column} is mapped more than once`
      : watchedColumnProblem(entry, byColumn);
    return message ? [{ index, message }] : [];
  });
}

export const UploadLayoutDefinitionSchema = z
  .object({
    /**
     * A row is written only if at least one of these workbook columns is
     * filled; the rest are listed as skipped. Empty = every non-blank row.
     */
    includeWhenAnyFilled: z.array(HeaderNameSchema).max(20),
    /**
     * A row is also skipped when any of these workbook columns holds a zero,
     * e.g. a core cost of 0 that cannot be priced.
     */
    skipWhenZero: z.array(HeaderNameSchema).max(20).optional(),
    /**
     * A row is also skipped when any of these workbook columns is blank, e.g.
     * a Pricing Factor left blank for an item that is not in the system.
     */
    skipWhenBlank: z.array(HeaderNameSchema).max(20).optional(),
    /**
     * A second workbook some suppliers send alongside the results: each row
     * is matched to the lookup row whose `keyColumn` equals the row's
     * `matchColumn`. Formulas and mappings read its columns as
     * `[<label>: <header>]`, e.g. `[Feedback: Margin Increase]`.
     */
    lookup: UploadLookupSchema.nullable().optional(),
    /** Upload columns not listed are written blank. */
    columns: z.array(UploadLayoutColumnSchema).max(UPLOAD_COLUMNS.length),
  })
  .superRefine(({ columns }, ctx) => {
    for (const { index, message } of uploadLayoutColumnProblems(columns)) {
      ctx.addIssue({ code: "custom", path: ["columns", index], message });
    }
  });

export const UploadLayoutSchema = z.object({
  id: z.string().uuid(),
  /** Usually the supplier's name, e.g. "Rockwell". */
  name: z.string(),
  definition: UploadLayoutDefinitionSchema,
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});

const LayoutNameSchema = z.string().trim().min(1).max(100);

export const CreateUploadLayoutInputSchema = z.object({
  name: LayoutNameSchema,
  definition: UploadLayoutDefinitionSchema,
});

export const UpdateUploadLayoutInputSchema = z.object({
  id: z.string().uuid(),
  name: LayoutNameSchema.optional(),
  definition: UploadLayoutDefinitionSchema.optional(),
});

/** Deletes one of the org's Results → Upload records (a rule or a layout) by id. */
export function deleteUploadToolRecordContract(
  summary: string,
  description: string,
) {
  return oc
    .input(z.object({ id: z.string().uuid() }))
    .output(z.object({ success: z.literal(true) }))
    .route({ method: "DELETE", summary, description });
}

export const uploadLayoutsContracts = {
  list: oc
    .input(z.object({}))
    .output(
      z.object({
        layouts: z.array(UploadLayoutSchema),
        /** Stored layouts this version cannot read; listed so they can be deleted. */
        unreadableLayoutIds: z.array(z.string().uuid()),
      }),
    )
    .route({
      method: "GET",
      summary: "List the org's Results → Upload supplier layouts",
      description:
        "Returns the organization's supplier layouts that map each supplier's results workbook columns onto the ERP price upload CSV, plus ids of stored layouts that can no longer be read.",
    }),
  create: oc
    .input(CreateUploadLayoutInputSchema)
    .output(z.object({ layout: UploadLayoutSchema }))
    .route({
      method: "POST",
      summary: "Create a supplier upload layout",
      description:
        "Saves a named layout describing how one supplier's results workbook fills each ERP price upload column. Formulas are validated; invalid layouts are rejected.",
    }),
  update: oc
    .input(UpdateUploadLayoutInputSchema)
    .output(z.object({ layout: UploadLayoutSchema }))
    .route({
      method: "PATCH",
      summary: "Update a supplier upload layout",
      description:
        "Renames a supplier upload layout or replaces its column mapping definition. Formulas are validated; invalid layouts are rejected.",
    }),
  delete: deleteUploadToolRecordContract(
    "Delete a supplier upload layout",
    "Permanently removes a supplier upload layout; validation rules scoped to that layout are deleted with it.",
  ),
} as const;

export type UploadColumn = z.infer<typeof UploadColumnSchema>;
export type UploadLookup = z.infer<typeof UploadLookupSchema>;
export type UploadSourceTransform = z.infer<typeof UploadSourceTransformSchema>;
export type UploadColumnMapping = z.infer<typeof UploadColumnMappingSchema>;
export type UploadLayoutColumn = z.infer<typeof UploadLayoutColumnSchema>;
export type UploadLayoutDefinition = z.infer<
  typeof UploadLayoutDefinitionSchema
>;
export type UploadLayout = z.infer<typeof UploadLayoutSchema>;
export type CreateUploadLayoutInput = z.infer<
  typeof CreateUploadLayoutInputSchema
>;
export type UpdateUploadLayoutInput = z.infer<
  typeof UpdateUploadLayoutInputSchema
>;
